/**
 * Formats date/time for calendar URLs
 * Handles timezone properly if provided
 */
const formatCalendarDateTime = (date, time, timezone) => {
  if (!date) return '';
  
  let dateObj;
  if (time) {
    // Create date with time in the specified timezone
    const dateTimeString = `${date}T${time}`;
    dateObj = new Date(dateTimeString);
  } else {
    dateObj = new Date(date);
  }
  
  // Return ISO format without timezone (assumes local or specified timezone)
  return dateObj.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
};

/**
 * Format event description for Google Calendar (supports HTML)
 */
const formatGoogleDescription = (event, eventUrl) => {
  const formattedDescription = htmlToGoogleFormat(event.description);
  const parts = [];
  
  if (formattedDescription) {
    parts.push(formattedDescription);
  }
  
  // Add event details with HTML breaks
  const details = [];
  if (event.hostName) {
    details.push(`<b>Host:</b> ${event.hostName}`);
  }
  
  if (event.locationName || event.location) {
    details.push(`<b>Location:</b> ${event.locationName || event.location}`);
  }
  
  if (details.length > 0) {
    if (formattedDescription) {
      parts.push('<br><br>'); // Only add space if there was a description
    }
    parts.push(details.join('<br>'));
  }
  
  // Add event link
  if (formattedDescription || details.length > 0) {
    parts.push('<br><br>');
  }
  parts.push(`<a href="${eventUrl}">View Event Details</a>`);
  
  if (!event.isPrivate) {
    parts.push('<br>');
    parts.push(`<a href="${eventUrl}">RSVP Here</a>`);
  }
  
  return parts.join('');
};

/**
 * Format event description for Outlook/Yahoo (plain text with line breaks)
 */
const formatPlainDescription = (event, eventUrl) => {
  const strippedDescription = stripHtml(event.description);
  const parts = [];
  
  if (strippedDescription) {
    parts.push(strippedDescription);
    parts.push('');
    parts.push('');
  }
  
  // Add event details
  if (event.hostName) {
    parts.push(`Host: ${event.hostName}`);
  }
  
  if (event.locationName || event.location) {
    parts.push(`Location: ${event.locationName || event.location}`);
  }
  
  // Add event link
  parts.push('');
  parts.push(`View event details: ${eventUrl}`);
  
  if (!event.isPrivate) {
    parts.push(`RSVP at: ${eventUrl}`);
  }
  
  return parts.join('\n');
};

/**
 * Generate Google Calendar URL
 * Google Calendar has the best support for formatting (HTML)
 */
export const getGoogleCalendarUrl = (event, eventUrl) => {
  const startDateTime = formatCalendarDateTime(event.startDate, event.startTime, event.timezone);
  const endDateTime = formatCalendarDateTime(
    event.endDate || event.startDate, 
    event.endTime || event.startTime, 
    event.timezone
  );
  
  const description = formatGoogleDescription(event, eventUrl);
  const location = event.location || event.locationName || '';
  
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.name,
    dates: `${startDateTime}/${endDateTime}`,
    details: description,
    location: location,
  });
  
  // Add timezone if available
  if (event.timezone) {
    params.append('ctz', event.timezone);
  }
  
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

/**
 * Generate Outlook Calendar URL
 * Outlook.com supports plain text with line breaks
 */
export const getOutlookCalendarUrl = (event, eventUrl) => {
  const startDateTime = formatCalendarDateTime(event.startDate, event.startTime, event.timezone);
  const endDateTime = formatCalendarDateTime(
    event.endDate || event.startDate, 
    event.endTime || event.startTime, 
    event.timezone
  );
  
  const description = formatPlainDescription(event, eventUrl);
  const location = event.location || event.locationName || '';
  
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: event.name,
    startdt: startDateTime,
    enddt: endDateTime,
    body: description,
    location: location,
  });
  
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
};

/**
 * Generate Yahoo Calendar URL
 * Yahoo has more limited formatting support
 */
export const getYahooCalendarUrl = (event, eventUrl) => {
  const startDateTime = formatCalendarDateTime(event.startDate, event.startTime, event.timezone);
  const endDateTime = formatCalendarDateTime(
    event.endDate || event.startDate, 
    event.endTime || event.startTime, 
    event.timezone
  );
  
  const description = formatPlainDescription(event, eventUrl);
  const location = event.location || event.locationName || '';
  
  const params = new URLSearchParams({
    v: '60',
    title: event.name,
    st: startDateTime,
    et: endDateTime,
    desc: description,
    in_loc: location,
  });
  
  return `https://calendar.yahoo.com/?${params.toString()}`;
};

/**
 * Helper to strip HTML tags while preserving line breaks
 */
const stripHtml = (html) => {
  if (!html) return '';
  
  // Replace block elements with line breaks
  let text = html
    .replace(/<\/p>/gi, '<br><br>')
    .replace(/<\/div>/gi, '<br>')
    .replace(/<\/li>/gi, '<br>')
    .replace(/<\/h[1-6]>/gi, '<br><br>')
    .replace(/<br\s*\/?>/gi, '<br>');
  
  // Now strip remaining HTML tags
  const doc = new DOMParser().parseFromString(text, 'text/html');
  return doc.body.textContent || '';
};

/**
 * Helper to convert HTML to formatted text with HTML breaks for Google Calendar
 */
const htmlToGoogleFormat = (html) => {
  if (!html) return '';
  
  // First, remove empty paragraphs and divs that just contain whitespace or &nbsp;
  let text = html.replace(/<p>(&nbsp;|\s|<br\s*\/?>)*<\/p>/gi, '');
  text = text.replace(/<div>(&nbsp;|\s|<br\s*\/?>)*<\/div>/gi, '');
  
  // Handle blockquotes with visual formatting (italics and quotes)
  text = text.replace(/<blockquote>([\s\S]*?)<\/blockquote>/gi, (match, content) => {
    // First convert strong/em to b/i
    let cleanContent = content
      .replace(/<strong>/gi, '<b>')
      .replace(/<\/strong>/gi, '</b>')
      .replace(/<em>/gi, '<i>')
      .replace(/<\/em>/gi, '</i>');
    
    // Remove other HTML tags but keep b, i, br
    cleanContent = cleanContent.replace(/<(?!\/?(?:b|i|br)\b)[^>]+>/gi, '');
    
    // Clean up excessive breaks inside blockquote
    cleanContent = cleanContent.replace(/(<br>\s*){2,}/gi, ' ');
    cleanContent = cleanContent.replace(/<br>/gi, ' ');
    
    // Replace multiple spaces with single space
    cleanContent = cleanContent.replace(/\s+/g, ' ');
    
    // Trim whitespace and existing quotes
    cleanContent = cleanContent.trim().replace(/^["'\s]+|["'\s]+$/g, '');
    
    return cleanContent ? `<br><br><i>"${cleanContent}"</i><br><br>` : '';
  });
  
  // Replace block elements with proper HTML breaks
  text = text
    .replace(/<\/p>/gi, '<br><br>')
    .replace(/<\/div>/gi, '<br>')
    .replace(/<\/li>/gi, '<br>')
    .replace(/<\/h[1-6]>/gi, '<br><br>')
    .replace(/<br\s*\/?>/gi, '<br>');
  
  // Convert bold/italic tags to proper format
  text = text
    .replace(/<strong>/gi, '<b>')
    .replace(/<\/strong>/gi, '</b>')
    .replace(/<em>/gi, '<i>')
    .replace(/<\/em>/gi, '</i>');
  
  // Strip all other HTML tags but keep b, i, br, a tags
  text = text.replace(/<(?!\/?(?:b|i|br|a)\b)[^>]+>/gi, '');
  
  // Clean up excessive line breaks (more than 2 in a row)
  text = text.replace(/(<br>\s*){3,}/gi, '<br><br>');
  
  // Clean up any whitespace-only text between breaks
  text = text.replace(/<br>(\s|&nbsp;)+<br>/gi, '<br><br>');
  
  // Trim leading/trailing breaks and whitespace
  text = text.replace(/^(<br>|&nbsp;|\s)+|(<br>|&nbsp;|\s)+$/gi, '');
  
  return text;
};

/**
 * Check if Web Share API is available
 */
export const canUseWebShare = () => {
  return typeof navigator !== 'undefined' && navigator.share;
};

/**
 * Share event using Web Share API (mobile)
 */
export const shareViaWebShare = async (event, eventUrl) => {
  if (!canUseWebShare()) {
    return { success: false, error: 'Web Share API not supported' };
  }
  
  try {
    await navigator.share({
      title: event.name,
      text: `Join me at ${event.name}`,
      url: eventUrl,
    });
    return { success: true };
  } catch (error) {
    if (error.name === 'AbortError') {
      // User cancelled the share
      return { success: false, error: 'Share cancelled' };
    }
    return { success: false, error: error.message };
  }
};
